import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

function DesignReview({ apiUrl, getStatusBadge, formatStatus }) {
  const { pieceId } = useParams()
  const navigate = useNavigate()
  const [piece, setPiece] = useState(null)
  const [designs, setDesigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedDesign, setSelectedDesign] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchPiece()
  }, [pieceId])

  const fetchPiece = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/content/${pieceId}`)
      const data = await res.json()
      setPiece(data.piece)
      setDesigns(data.designs || [])
    } catch (e) {
      console.error('Failed to fetch:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (designId) => {
    setSubmitting(true)
    try {
      const res = await fetch(`${apiUrl}/api/content/${pieceId}/approve-design`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ design_id: designId })
      })
      
      if (res.ok) {
        alert('Design approved! Content will be written to sheets.')
        navigate('/')
      }
    } catch (e) {
      console.error('Failed to approve:', e)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (<div className="text-center py-12">Loading...</div>)
  }

  if (!piece) {
    return (<div className="text-center py-12 text-red-500">Content piece not found</div>)
  }

  return (
    <div>
      <div className="mb-6">
        <button onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700">
          &larr; Back to Queue
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold">Design Review</h1>
            <p className="text-gray-500">{piece.content_bucket} {piece.industry && `· ${piece.industry}`}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(piece.status)}`}>
            {formatStatus(piece.status)}
          </span>
        </div>

        <div className="bg-blue-50 p-4 rounded mb-4">
          <p className="font-medium mb-2">Approved Copy:</p>
          <p className="text-gray-700 whitespace-pre-wrap line-clamp-4">{piece.content_brief}</p>
        </div>
      </div>

      <h2 className="text-lg font-semibold mb-4">Design Variants ({designs.length})</h2>

      {designs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>No designs generated yet.</p>
          <p className="text-sm mt-2">Designs will appear here once the Design Agent completes.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {designs.map((design) => (
            <div 
              key={design.id}
              className={`bg-white rounded-lg shadow p-4 border-2 transition-colors ${
                selectedDesign === design.id ? 'border-brand-500' : 'border-transparent'
              }`}
            >
              <div className="text-sm font-bold text-brand-600 mb-3">
                DESIGN {design.version}
              </div>

              {design.thumbnail_url ? (
                <img 
                  src={design.thumbnail_url}
                  alt={`Design ${design.version}`}
                  className="w-full h-48 object-cover rounded mb-3"
                />
              ) : (
                <div className="w-full h-48 bg-gray-100 rounded flex items-center justify-center mb-3">
                  <span className="text-gray-400">Design Preview</span>
                </div>
              )}

              <div className="text-sm text-gray-500 mb-3">
                Template: {design.template_name || 'Custom'}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedDesign(design.id)}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium ${
                    selectedDesign === design.id
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Select
                </button>
                
                {design.canva_url && (
                  <a
                    href={design.canva_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-3 py-2 rounded text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 text-center"
                  >
                    View in Canva
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedDesign && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-6">
          <p className="font-medium text-green-800 mb-4">
            Approve Design {designs.find(d => d.id === selectedDesign)?.version}?
          </p>
          <p className="text-sm text-green-700 mb-4">
            This will write the final content to Google Sheets.
          </p>
          <button
            onClick={() => handleApprove(selectedDesign)}
            disabled={submitting}
            className="bg-green-600 text-white px-6 py-2 rounded font-medium hover:bg-green-700 disabled:opacity-50"
          >
            {submitting ? 'Finalizing...' : 'Approve & Finalize'}
          </button>
        </div>
      )}
    </div>
  )
}

export default DesignReview
